import { useFormikEnhanced } from '@superdispatch/forms';
import { DrawerActions, useSnackbarStack } from '@superdispatch/ui';
import { Button } from '@superdispatch/ui-lab';
import { toFormikValidationSchema } from 'shared/form/FormSchema';
import { SlowFormikDrawer } from 'shared/form/SlowFormikDrawer';
import {
  useSubscriptionAPI,
  useSubscriptionDetails,
} from 'shared/modules/subscription/SubscriptionAPI';
import { subscriptionContactSchema } from 'shared/modules/subscription/SubscriptionDTO';
import { SubscriptionContactFields } from 'shared/modules/subscription/core/SubscriptionContactFields';

const validationSchema = toFormikValidationSchema(subscriptionContactSchema);

interface SubscriptionContactDrawerProps {
  open: boolean;
  onClose: () => void;
}

export function SubscriptionContactDrawer({
  open,
  onClose,
}: SubscriptionContactDrawerProps) {
  const { addSnackbar } = useSnackbarStack();
  const { data: subscriptionDetails } = useSubscriptionDetails();
  const { updateSubscriptionContact } = useSubscriptionAPI();
  const subscriptionContact = subscriptionDetails?.subscription?.contact;

  const formik = useFormikEnhanced({
    key: open,
    validateOnBlur: false,
    validationSchema,
    initialValues: {
      contact_first_name: subscriptionContact?.first_name || '',
      contact_last_name: subscriptionContact?.last_name || '',
      contact_email: subscriptionContact?.email || '',
    },
    onSubmit: (values) =>
      updateSubscriptionContact({
        first_name: values.contact_first_name.trim(),
        last_name: values.contact_last_name.trim(),
        email: values.contact_email.trim(),
      }),
    onSubmitSuccess: () => {
      onClose();
      addSnackbar('Subscription Contact was successfully updated', {
        variant: 'success',
      });
    },
    onSubmitFailure: (error) => {
      addSnackbar(error.message, { variant: 'error' });
    },
  });

  return (
    <SlowFormikDrawer
      formik={formik}
      open={open}
      onClose={onClose}
      title={
        subscriptionContact
          ? 'Edit Subscription Contact'
          : 'Add Subscription Contact'
      }
      actions={
        <DrawerActions>
          <Button
            type="submit"
            disabled={!formik.dirty}
            pending={formik.isSubmitting}
          >
            Save
          </Button>
        </DrawerActions>
      }
    >
      <SubscriptionContactFields />
    </SlowFormikDrawer>
  );
}
